import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiRequest } from "../../../../shared/api/client";
import { can, type SessionUser } from "../../../../shared/auth/access";
import {
  type AutocompleteOption,
} from "../../../../shared/components/AutocompleteSelect";
import { createSubmitSpecialistDecision } from "../../application/commands/submit-specialist-decision";
import type { CorrectionTask } from "../../domain/models";
import type { OfferEvaluation, OfferRow } from "../../domain/offer-evaluation";
import { procurementCommandGateway } from "../../infrastructure/api/procurement-command-gateway";
import {
  type PurchaseRequest,
} from "../../model";
import {
  findDuplicateSupplierOffer,
  getOfferDeliveryWarning,
  resolveOfferEditingId,
  saveOfferEntry,
  validateOfferEntry,
} from "../../offer-entry";
import { supplierMatchesRequestCategory } from "../../supplier-category-match";

const submitSpecialistDecision = createSubmitSpecialistDecision(procurementCommandGateway);

function editableRecommendationRationale(
  recommendation: OfferEvaluation["recommendation"],
) {
  return recommendation?.rationale ?? "";
}

export type OffersModuleProps = {
  notify: (message: string) => void;
  user: SessionUser;
  initialRequestNo?: string;
  dataRefreshVersion: number;
  onOpenPurchaseOrder: (requestNo: string) => void;
  onSpecialistDecisionSubmitted: () => void;
};

export function useOffersWorkspace({
  notify,
  user,
  initialRequestNo,
  dataRefreshVersion,
  onOpenPurchaseOrder,
  onSpecialistDecisionSubmitted,
}: OffersModuleProps) {
  const [rows, setRows] = useState<OfferRow[]>([]);
  const [query, setQuery] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const [savingOffer, setSavingOffer] = useState(false);
  const [offerFormError, setOfferFormError] = useState("");
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
  const [pendingOfferRemovalId, setPendingOfferRemovalId] = useState<
    string | null
  >(null);
  const [evaluation, setEvaluation] = useState<OfferEvaluation | null>(null);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [activeRequestNo, setActiveRequestNo] = useState("");
  const [recommendationOfferId, setRecommendationOfferId] = useState("");
  const [recommendationRationale, setRecommendationRationale] = useState("");
  const [decisionMode, setDecisionMode] = useState<"SYSTEM" | "ALTERNATIVE">(
    "SYSTEM",
  );
  const [decisionEditorOpen, setDecisionEditorOpen] = useState(false);
  const [completedDecisionOpen, setCompletedDecisionOpen] = useState(false);
  const [purchaseOrderLink, setPurchaseOrderLink] = useState<{
    state: "idle" | "checking" | "candidate" | "existing" | "unavailable";
    orderNo?: string;
  }>({ state: "idle" });
  const [routingForApproval, setRoutingForApproval] = useState(false);
  const [offerDocument, setOfferDocument] = useState<File | null>(null);
  const [documentsRefreshVersion, setDocumentsRefreshVersion] = useState(0);
  const [extraCostsOpen, setExtraCostsOpen] = useState(false);
  const [requestOptions, setRequestOptions] = useState<PurchaseRequest[]>([]);
  const [correctionTasks, setCorrectionTasks] = useState<CorrectionTask[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<
    Array<{
      id: string;
      name: string;
      category: string;
      categories?: readonly string[];
    }>
  >([]);
  const requestAutocompleteOptions = useMemo<AutocompleteOption[]>(
    () =>
      requestOptions.map((request) => ({
        value: request.id,
        label: `${request.id} · ${request.title}`,
        description: `${request.department} · ${request.meta}`,
        keywords: [request.title, request.department, request.requester],
      })),
    [requestOptions],
  );
  const [form, setForm] = useState({
    requestNo: "",
    supplierId: "",
    unitPrice: "",
    currency: "TRY",
    requestedQuantity: "",
    unit: "adet",
    paymentDays: "",
    deliveryDays: "",
    procurementNote: "",
    validUntil: "",
    shippingCost: "",
    insuranceCost: "",
    installationCost: "",
    otherCost: "",
    otherCostDescription: "",
  });
  const selectedFormRequest = requestOptions.find(
    (request) => request.id === form.requestNo,
  );
  const supplierAutocompleteOptions = useMemo<AutocompleteOption[]>(
    () =>
      supplierOptions
        .filter((supplier) =>
          supplierMatchesRequestCategory(
            selectedFormRequest?.category,
            supplier.categories ?? supplier.category,
          ),
        )
        .map((supplier) => ({
          value: supplier.id,
          label: supplier.name,
          description: (supplier.categories ?? [supplier.category]).join(" · "),
          keywords: [...(supplier.categories ?? [supplier.category])],
        })),
    [selectedFormRequest?.category, supplierOptions],
  );
  useEffect(() => {
    Promise.all([
      apiRequest<OfferRow[]>("/api/offers"),
      can(user, "offer.read")
        ? apiRequest<PurchaseRequest[]>("/api/requests")
        : Promise.resolve([]),
      can(user, "offer.manage")
        ? apiRequest<
          Array<{
            id: string;
            name: string;
            category: string;
            categories?: readonly string[];
          }>
        >("/api/suppliers")
        : Promise.resolve([]),
      user.role === "SATIN_ALMA"
        ? apiRequest<CorrectionTask[]>("/api/corrections")
        : Promise.resolve([]),
    ])
      .then(([offers, purchaseRequests, suppliers, corrections]) => {
        const preferredRequestNo =
          (initialRequestNo &&
            purchaseRequests.some((request) => request.id === initialRequestNo)
            ? initialRequestNo
            : "") ||
          purchaseRequests.find((request) => request.offers > 0)?.id ||
          purchaseRequests[0]?.id ||
          "";
        setRows(offers);
        setRequestOptions(purchaseRequests);
        setSupplierOptions(suppliers);
        setCorrectionTasks(corrections);
        setActiveRequestNo((current) => current || preferredRequestNo);
        setForm((current) => {
          const requestNo =
            current.requestNo &&
              purchaseRequests.some((request) => request.id === current.requestNo)
              ? current.requestNo
              : preferredRequestNo;
          const selectedRequest = purchaseRequests.find(
            (request) => request.id === requestNo,
          );
          return {
            ...current,
            requestNo,
            supplierId: current.supplierId,
            requestedQuantity: selectedRequest?.quantity
              ? String(selectedRequest.quantity)
              : "",
            unit: selectedRequest?.unit || "adet",
          };
        });
      })
      .catch(() => notify("Teklifler sunucudan alınamadı."));
  }, [dataRefreshVersion, initialRequestNo, notify, user]);
  useEffect(() => {
    if (!activeRequestNo) {
      setEvaluation(null);
      return;
    }
    let active = true;
    setEvaluationLoading(true);
    apiRequest<OfferEvaluation>(
      `/api/requests/${activeRequestNo}/evaluations/latest`,
    )
      .then((result) => {
        if (!active) return;
        setEvaluation(result);
        setRecommendationOfferId(
          result.recommendation?.offerId ??
          result.topOfferId ??
          (result.status === "SINGLE_OFFER"
            ? result.offers.find((offer) => !offer.exclusionReasons.length)
              ?.id
            : "") ??
          "",
        );
        setRecommendationRationale(
          editableRecommendationRationale(result.recommendation),
        );
        setDecisionMode(
          result.recommendation?.decisionType === "SELECT_ALTERNATIVE"
            ? "ALTERNATIVE"
            : "SYSTEM",
        );
        setDecisionEditorOpen(false);
      })
      .catch(() => {
        if (active) setEvaluation(null);
      })
      .finally(() => {
        if (active) setEvaluationLoading(false);
      });
    return () => {
      active = false;
    };
  }, [activeRequestNo, dataRefreshVersion]);
  const visibleRows = rows.filter(
    (offer) =>
      (!activeRequestNo || offer.requestNo === activeRequestNo) &&
      `${offer.id} ${offer.request} ${offer.supplier}`
        .toLocaleLowerCase("tr")
        .includes(query.toLocaleLowerCase("tr")),
  );
  const selectedOfferRequest = requestOptions.find(
    (request) => request.id === form.requestNo,
  );
  const editingOffer = rows.find((offer) => offer.id === editingOfferId);
  const effectiveEditingOfferId = resolveOfferEditingId(
    editingOfferId,
    editingOffer?.supplierId ?? "",
    form.supplierId,
  );
  const createsNewSupplierOffer = Boolean(
    editingOfferId && !effectiveEditingOfferId,
  );
  const deliveryWarning = getOfferDeliveryWarning(
    form.deliveryDays,
    selectedOfferRequest?.neededAt ?? "",
  );
  const duplicateSupplierOffer = findDuplicateSupplierOffer(
    rows,
    form.requestNo,
    form.supplierId,
    effectiveEditingOfferId,
  );
  const closeOfferComposer = () => {
    setComposerOpen(false);
    setOfferFormError("");
    setEditingOfferId(null);
    setOfferDocument(null);
    setExtraCostsOpen(false);
  };
  const refreshOfferWorkspace = async (requestNo: string) => {
    const [offers, purchaseRequests, refreshedEvaluation, corrections] =
      await Promise.all([
        apiRequest<OfferRow[]>("/api/offers"),
        apiRequest<PurchaseRequest[]>("/api/requests"),
        apiRequest<OfferEvaluation>(
          `/api/requests/${requestNo}/evaluations/latest`,
        ).catch(() => null),
        user.role === "SATIN_ALMA"
          ? apiRequest<CorrectionTask[]>("/api/corrections")
          : Promise.resolve([]),
      ]);
    setRows(offers);
    setRequestOptions(purchaseRequests);
    setEvaluation(refreshedEvaluation);
    setCorrectionTasks(corrections);
    setActiveRequestNo(requestNo);
    setDocumentsRefreshVersion((current) => current + 1);
    return refreshedEvaluation;
  };
  const openOfferEditor = (offer: OfferRow) => {
    const requestedQuantity = Number(offer.requestedQuantity);
    const persistedUnitPrice = Number(offer.unitPrice);
    const editableUnitPrice =
      Number.isFinite(persistedUnitPrice) && persistedUnitPrice > 0
        ? persistedUnitPrice
        : Number.isFinite(offer.amountValue) && requestedQuantity > 0
          ? offer.amountValue / requestedQuantity
          : "";
    setEditingOfferId(offer.id);
    setOfferDocument(null);
    setForm({
      requestNo: offer.requestNo,
      supplierId: offer.supplierId,
      unitPrice: String(editableUnitPrice),
      currency: offer.currency,
      requestedQuantity: String(offer.requestedQuantity ?? ""),
      unit: offer.unit ?? "adet",
      paymentDays: String(
        offer.paymentDays ?? Number.parseInt(offer.payment, 10),
      ),
      deliveryDays: String(
        offer.deliveryDays ?? Number.parseInt(offer.delivery, 10),
      ),
      procurementNote: offer.procurementNote,
      validUntil: offer.validUntil ?? "",
      shippingCost: String(offer.shippingCost ?? ""),
      insuranceCost: String(offer.insuranceCost ?? ""),
      installationCost: String(offer.installationCost ?? ""),
      otherCost: String(offer.otherCost ?? ""),
      otherCostDescription: offer.otherCostDescription ?? "",
    });
    setExtraCostsOpen(
      [
        offer.shippingCost,
        offer.insuranceCost,
        offer.installationCost,
        offer.otherCost,
      ].some((value) => value !== null && value !== undefined),
    );
    setComposerOpen(true);
  };
  const applyRefreshedEvaluation = (
    refreshedEvaluation: OfferEvaluation | null,
  ) => {
    if (!refreshedEvaluation) return;
    setRecommendationOfferId(
      refreshedEvaluation.recommendation?.offerId ??
      refreshedEvaluation.topOfferId ??
      (refreshedEvaluation.status === "SINGLE_OFFER"
        ? refreshedEvaluation.offers.find(
          (offer) => !offer.exclusionReasons.length,
        )?.id
        : "") ??
      "",
    );
    setRecommendationRationale(
      editableRecommendationRationale(refreshedEvaluation.recommendation),
    );
    setDecisionMode(
      refreshedEvaluation.recommendation?.decisionType === "SELECT_ALTERNATIVE"
        ? "ALTERNATIVE"
        : "SYSTEM",
    );
  };
  const saveOffer = async () => {
    if (savingOffer) return;
    setOfferFormError("");
    if (duplicateSupplierOffer) {
      const message = `Bu tedarikçinin bu talep için zaten ${duplicateSupplierOffer.id} numaralı teklifi var. Mevcut teklifi düzenleyin.`;
      setOfferFormError(message);
      notify(`Hata: ${message}`);
      return;
    }
    const validationError = validateOfferEntry({
      requestNo: form.requestNo,
      supplierId: form.supplierId,
      unitPrice: form.unitPrice,
      currency: form.currency as "TRY" | "EUR" | "USD",
      paymentDays: form.paymentDays,
      deliveryDays: form.deliveryDays,
      procurementNote: form.procurementNote,
      validUntil: form.validUntil,
      shippingCost: form.shippingCost,
      insuranceCost: form.insuranceCost,
      installationCost: form.installationCost,
      otherCost: form.otherCost,
      otherCostDescription: form.otherCostDescription,
    });
    if (validationError) {
      setOfferFormError(validationError);
      notify(`Hata: ${validationError}`);
      return;
    }
    if (offerDocument && offerDocument.size > 20 * 1024 * 1024) {
      const message = "Teklif belgesi en fazla 20 MB olabilir.";
      setOfferFormError(message);
      notify(`Hata: ${message}`);
      return;
    }
    setSavingOffer(true);
    try {
      const wasEditing = Boolean(effectiveEditingOfferId);
      const requestNo = form.requestNo;
      const { saved, documentUpload } = await saveOfferEntry<OfferRow>({
        input: {
          ...form,
          currency: form.currency as "TRY" | "EUR" | "USD",
        },
        editingOfferId: effectiveEditingOfferId,
        document: offerDocument,
      });
      setDecisionEditorOpen(false);
      closeOfferComposer();
      setForm((current) => ({
        ...current,
        unitPrice: "",
        paymentDays: "",
        deliveryDays: "",
        procurementNote: "",
        validUntil: "",
        shippingCost: "",
        insuranceCost: "",
        installationCost: "",
        otherCost: "",
        otherCostDescription: "",
      }));
      notify(
        documentUpload
          ? `${saved.id} numaralı teklif ${wasEditing ? "güncellendi" : "kaydedildi"}; belge arka planda yükleniyor.`
          : `${saved.id} numaralı teklif ${wasEditing ? "güncellendi" : "kaydedildi"}.`,
      );
      const refreshedEvaluation = await refreshOfferWorkspace(requestNo);
      applyRefreshedEvaluation(refreshedEvaluation);
      notify(
        documentUpload
          ? `${saved.id} numaralı teklif hazır; belge yüklemesi sürüyor.`
          : refreshedEvaluation
            ? `${saved.id} numaralı teklif hazır; sistem değerlendirmesi yenilendi.`
            : `${saved.id} numaralı teklif hazır; otomatik değerlendirme daha sonra yeniden denenecek.`,
      );
      if (documentUpload) {
        void documentUpload
          .then(async () => {
            const evaluation = await refreshOfferWorkspace(requestNo);
            applyRefreshedEvaluation(evaluation);
            notify(`${saved.id} numaralı teklifin belgesi kullanıma hazır.`);
          })
          .catch(async (error) => {
            await refreshOfferWorkspace(requestNo).catch(() => undefined);
            notify(
              `${saved.id} numaralı teklif kaydedildi, ancak belge eklenemedi: ${error instanceof Error ? error.message : "Belge yükleme hatası."}`,
            );
          });
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Teklif kaydedilemedi.";
      setOfferFormError(message);
      notify(`Hata: ${message}`);
    } finally {
      setSavingOffer(false);
    }
  };
  const removeOffer = async (offer: OfferRow) => {
    try {
      await apiRequest(`/api/offers/${encodeURIComponent(offer.id)}`, {
        method: "DELETE",
      });
      await refreshOfferWorkspace(offer.requestNo);
      setPendingOfferRemovalId(null);
      notify(`${offer.id} numaralı teklif kaldırıldı; denetim izi korundu.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Teklif kaldırılamadı.");
    }
  };
  const submitDecisionToManager = async () => {
    if (!evaluation || routingForApproval) return;
    const decisionType =
      evaluation.status === "SINGLE_OFFER"
        ? "SINGLE_SOURCE"
        : decisionMode === "ALTERNATIVE"
          ? "SELECT_ALTERNATIVE"
          : "ACCEPT_SYSTEM";
    setRoutingForApproval(true);
    try {
      const { recommendation } = await submitSpecialistDecision<
        OfferEvaluation["recommendation"]
      >({
        requestNo: activeRequestNo,
        evaluationId: evaluation.id,
        decisionType,
        offerId:
          decisionType === "ACCEPT_SYSTEM" ? undefined : recommendationOfferId,
        rationale: recommendationRationale.trim(),
      });
      setEvaluation((current) =>
        current ? { ...current, recommendation } : current,
      );
      setRequestOptions((current) =>
        current.map((request) =>
          request.id === activeRequestNo
            ? { ...request, status: "Onay bekliyor" }
            : request,
        ),
      );
      setDecisionEditorOpen(false);
      setCorrectionTasks((current) =>
        current.filter((task) => task.requestNo !== activeRequestNo),
      );
      notify("Uzman kararı kaydedildi ve Satın Alma Müdürüne iletildi.");
      onSpecialistDecisionSubmitted();
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "Karar Satın Alma Müdürüne iletilemedi.",
      );
    } finally {
      setRoutingForApproval(false);
    }
  };
  const activeRequest =
    requestOptions.find((request) => request.id === activeRequestNo) ?? null;
  const completedGeneralManagerDecision = activeRequest?.approvalChain?.find(
    (approval) => approval.role === "GENEL_MUDUR",
  );
  const completedManagerDecision = activeRequest?.approvalChain?.find(
    (approval) => approval.role === "SATIN_ALMA_MUDURU",
  );
  const completedFinalOffer = rows.find(
    (offer) => offer.id === completedGeneralManagerDecision?.decidedOfferId,
  );
  const completedSpecialistOffer = rows.find(
    (offer) => offer.id === evaluation?.recommendation?.offerId,
  );
  const activeCorrection =
    correctionTasks.find((task) => task.requestNo === activeRequestNo) ?? null;
  const approvalStarted = activeRequest
    ? ["Onay bekliyor", "Onaylandı", "Reddedildi"].includes(
      activeRequest.status,
    )
    : false;
  useEffect(() => {
    if (!approvalStarted) return;
    setComposerOpen(false);
    setDecisionEditorOpen(false);
    setPendingOfferRemovalId(null);
  }, [approvalStarted]);
  useEffect(() => {
    setCompletedDecisionOpen(false);
  }, [activeRequestNo]);
  useEffect(() => {
    if (
      activeRequest?.status !== "Onaylandı" ||
      !activeRequestNo ||
      !can(user, "purchase_order.read")
    ) {
      setPurchaseOrderLink({ state: "idle" });
      return;
    }
    let active = true;
    setPurchaseOrderLink({ state: "checking" });
    Promise.all([
      apiRequest<Array<{ requestNo: string; orderNo: string; }>>(
        "/api/purchase-orders",
      ),
      can(user, "purchase_order.create")
        ? apiRequest<Array<{ requestNo: string; }>>(
          "/api/purchase-orders/candidates",
        )
        : Promise.resolve([]),
    ])
      .then(([orders, candidates]) => {
        if (!active) return;
        const order = orders.find((item) => item.requestNo === activeRequestNo);
        if (order) {
          setPurchaseOrderLink({
            state: "existing",
            orderNo: order.orderNo,
          });
          return;
        }
        setPurchaseOrderLink({
          state: candidates.some((item) => item.requestNo === activeRequestNo)
            ? "candidate"
            : "unavailable",
        });
      })
      .catch(() => {
        if (active) setPurchaseOrderLink({ state: "unavailable" });
      });
    return () => {
      active = false;
    };
  }, [activeRequest?.status, activeRequestNo, dataRefreshVersion, user]);
  const evaluationReady = Boolean(
    evaluation && ["COMPLETED", "SINGLE_OFFER"].includes(evaluation.status),
  );
  const canManageActiveOffers =
    can(user, "offer.manage") && Boolean(activeRequest) && !approvalStarted;
  const decisionCanBeSubmitted = Boolean(
    evaluation &&
    activeRequestNo &&
    recommendationOfferId &&
    recommendationRationale.trim().length >= 10 &&
    (!activeCorrection || activeCorrection.hasOfferChange),
  );
  const systemOffer =
    evaluation?.offers.find((offer) => offer.id === evaluation.topOfferId) ??
    null;
  const alternativeOffers =
    evaluation?.offers
      .filter(
        (offer) => offer.rank !== null && offer.id !== evaluation.topOfferId,
      )
      .sort((left, right) => (left.rank ?? 99) - (right.rank ?? 99)) ?? [];
  const decisionOffers = systemOffer
    ? [systemOffer, ...alternativeOffers]
    : alternativeOffers;
  const systemRecommendationStrengths = systemOffer
    ? [
      systemOffer.amountScore === 100 ? "toplam maliyet" : null,
      systemOffer.deliveryScore === 100 ? "teslimat süresi" : null,
      systemOffer.paymentScore === 100 ? "ödeme vadesi" : null,
      systemOffer.performanceScore === 100 ? "tedarikçi performansı" : null,
    ].filter((value): value is string => Boolean(value))
    : [];
  const systemRecommendationSummary = systemOffer
    ? `${systemOffer.totalScore?.toLocaleString("tr-TR")} puanla ilk sırada. ${systemRecommendationStrengths.length
      ? `${systemRecommendationStrengths.join(", ")} kriterlerinde en güçlü teklif.`
      : "Fiyat, teslimat, vade ve kanıtlı performansın ağırlıklı toplamında en dengeli teklif."
    }`
    : "";
  const singleOffer =
    evaluation?.status === "SINGLE_OFFER"
      ? (evaluation.offers.find((offer) => !offer.exclusionReasons.length) ??
        null)
      : null;
  const excludedOffers =
    evaluation?.offers.filter((offer) => offer.exclusionReasons.length > 0) ??
    [];
  const evaluationBlockerReasons = [
    ...new Set(excludedOffers.flatMap((offer) => offer.exclusionReasons)),
  ];
  const evaluationStatusLabel = evaluation
    ? evaluationReady
      ? `Sürüm ${evaluation.version} · Hazır`
      : evaluation.status === "EXCHANGE_RATE_MISSING"
        ? "Kur bilgisi gerekli"
        : evaluation.status === "NO_ELIGIBLE_OFFER"
          ? "Uygun teklif bulunamadı"
          : "Teklif koşulları eksik"
    : "Teklif bekleniyor";
  const selectedDecisionOffer =
    evaluation?.offers.find((offer) => offer.id === recommendationOfferId) ??
    null;
  const activeRequestHasOffers = rows.some(
    (offer) => offer.requestNo === activeRequestNo,
  );
  const openNewOffer = () => {
    if (!activeRequest) return;
    setEditingOfferId(null);
    setOfferDocument(null);
    setForm((current) => ({
      ...current,
      requestNo: activeRequest.id,
      supplierId: "",
      requestedQuantity: activeRequest.quantity
        ? String(activeRequest.quantity)
        : "",
      unit: activeRequest.unit || "adet",
    }));
    setComposerOpen(true);
  };
  return {
    requestWorkspaceService,
    user,
    onOpenPurchaseOrder,
    rows,
    query,
    setQuery,
    composerOpen,
    savingOffer,
    offerFormError,
    editingOfferId,
    pendingOfferRemovalId,
    setPendingOfferRemovalId,
    evaluation,
    evaluationLoading,
    activeRequestNo,
    setActiveRequestNo,
    recommendationOfferId,
    setRecommendationOfferId,
    recommendationRationale,
    setRecommendationRationale,
    decisionMode,
    setDecisionMode,
    decisionEditorOpen,
    setDecisionEditorOpen,
    completedDecisionOpen,
    setCompletedDecisionOpen,
    purchaseOrderLink,
    routingForApproval,
    offerDocument,
    setOfferDocument,
    documentsRefreshVersion,
    extraCostsOpen,
    setExtraCostsOpen,
    requestOptions,
    requestAutocompleteOptions,
    form,
    setForm,
    supplierAutocompleteOptions,
    visibleRows,
    selectedOfferRequest,
    effectiveEditingOfferId,
    createsNewSupplierOffer,
    deliveryWarning,
    duplicateSupplierOffer,
    closeOfferComposer,
    openOfferEditor,
    saveOffer,
    removeOffer,
    submitDecisionToManager,
    activeRequest,
    completedGeneralManagerDecision,
    completedManagerDecision,
    completedFinalOffer,
    completedSpecialistOffer,
    activeCorrection,
    approvalStarted,
    evaluationReady,
    canManageActiveOffers,
    decisionCanBeSubmitted,
    systemOffer,
    alternativeOffers,
    decisionOffers,
    systemRecommendationSummary,
    singleOffer,
    excludedOffers,
    evaluationBlockerReasons,
    evaluationStatusLabel,
    selectedDecisionOffer,
    activeRequestHasOffers,
    openNewOffer,
  };
}
import { createRequestWorkspaceService } from "../../application/queries/request-workspace";
import { procurementQueryGateway } from "../../infrastructure/api/procurement-query-gateway";
const requestWorkspaceService = createRequestWorkspaceService(procurementQueryGateway);
